import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  TeacherPortalLanguageProvider,
  useTeacherPortalLanguage
} from './contexts/TeacherPortalLanguageContext';
import { TeacherPortalLoginPage } from './TeacherPortalLoginPage';
import { TeacherAcademySelectModal } from './TeacherAcademySelectModal';
import { TeacherPortalDashboard } from './TeacherPortalDashboard';
import { academyApi } from '../../services/academyApi';
import {
  Teacher, PortalAcademy, AcademySession, TeacherAbsenceRequest
} from '../../types/academy';

const formatLogoUrl = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `data:image/png;base64,${trimmed}`;
};

const TeacherPortalInner: React.FC = () => {
  const { t } = useTeacherPortalLanguage();

  const [token, setToken] = useState<string | null>(() => {
    const t = localStorage.getItem('blobtic_portal_teacher_token');
    return t && t !== 'undefined' && t !== 'null' ? t : null;
  });
  const [lastLoginIdent, setLastLoginIdent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(() => {
    const t = localStorage.getItem('blobtic_portal_teacher_token');
    return !!(t && t !== 'undefined' && t !== 'null');
  });
  const [error, setError] = useState<string | null>(null);

  // Portal State
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [canTakeAttendance, setCanTakeAttendance] = useState<boolean>(false);
  const [academies, setAcademies] = useState<PortalAcademy[]>([]);
  const [selectedAcademy, setSelectedAcademy] = useState<PortalAcademy | null>(null);
  const [showAcademyModal, setShowAcademyModal] = useState<boolean>(false);

  // Data
  const [groups, setGroups] = useState<any[]>([]);
  const [sessions, setSessions] = useState<AcademySession[]>([]);
  const [absenceRequests, setAbsenceRequests] = useState<TeacherAbsenceRequest[]>([]);
  const [finances, setFinances] = useState<any>({
    rate_type: 'hourly',
    hourly_rate: 0,
    total_hours_month: 0,
    total_amount_month: 0,
    history: []
  });

  const applyDashboardPayload = useCallback((d: any) => {
    if (!d) return;

    // 1. Teacher profile normalization
    const rawT = d.teacher || {};
    const rawPhoto = rawT.photo_base64 || rawT.TCH_PHOTO_BASE64 || rawT.photo || '';
    const formattedPhoto = rawPhoto && !rawPhoto.startsWith('data:') && !rawPhoto.startsWith('http')
      ? `data:image/jpeg;base64,${rawPhoto}`
      : rawPhoto;

    const mappedTeacher: Teacher = {
      ...rawT,
      id: rawT.id || rawT.TCH_IDF,
      first_name: rawT.first_name || rawT.TCH_FIRST_NAME || '',
      last_name: rawT.last_name || rawT.TCH_LAST_NAME || '',
      full_name: rawT.full_name || rawT.TCH_FULL_NAME || `${rawT.TCH_FIRST_NAME || ''} ${rawT.TCH_LAST_NAME || ''}`.trim(),
      email: rawT.email || rawT.TCH_EMAIL || '',
      phone: rawT.phone || rawT.TCH_PHONE || '',
      specialization: rawT.specialization || rawT.TCH_SPECIALIZATION || '',
      rate_type: rawT.rate_type || rawT.TCH_RATE_TYPE || 'hourly',
      hourly_rate: rawT.hourly_rate ?? rawT.TCH_HOURLY_RATE ?? 0,
      can_take_attendance: rawT.can_take_attendance ?? rawT.TCH_CAN_TAKE_ATTENDANCE ?? true,
      photo_base64: formattedPhoto,
      avatar_url: rawT.avatar_url || rawT.TCH_AVATAR_URL || '',
    };
    setTeacher(mappedTeacher);

    // 2. Attendance permission
    setCanTakeAttendance(Boolean(d.canTakeAttendance ?? d.can_take_attendance ?? mappedTeacher.can_take_attendance));

    // 3. Academy / Agency info
    const rawAca = d.agency || d.academy || {};
    const rawLogo = rawAca.logo_base64 || rawAca.logo || rawAca.dosLogo || rawAca.DOS_LOGO || '';
    const mappedAcademy: PortalAcademy = {
      dos_idf: rawAca.id || rawAca.dos_idf || rawAca.dosId || 1,
      dos_code: rawAca.code || rawAca.dos_code || rawAca.dosCode || '',
      dos_lib: rawAca.name || rawAca.dos_lib || rawAca.dosLib || '',
      address: rawAca.address || rawAca.dos_address || '',
      phone: rawAca.phone || rawAca.dos_tel || '',
      logo_base64: formatLogoUrl(rawLogo),
    };
    setSelectedAcademy(mappedAcademy);

    // 4. Groups normalization
    const mappedGroups = (d.groups || []).map((g: any) => ({
      ...g,
      id: g.id || g.GRP_IDF,
      name: g.name || g.GRP_NAME,
      status: g.status || g.GRP_STATUS,
      students_count: g.students_count ?? g.STUDENT_COUNT ?? (g.students ? g.students.length : 0),
      students: g.students || []
    }));
    setGroups(mappedGroups);

    // 5. Sessions normalization
    const mappedSessions: AcademySession[] = (d.sessions || []).map((s: any) => {
      const startDt = s.SSN_START_HOUR ? new Date(s.SSN_START_HOUR) : null;
      const endDt = s.SSN_END_HOUR ? new Date(s.SSN_END_HOUR) : null;
      const dateStr = s.date || (startDt ? startDt.toLocaleDateString('fr-CA') : '');
      const startTimeStr = s.start_time || (startDt ? startDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
      const endTimeStr = s.end_time || (endDt ? endDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

      return {
        ...s,
        id: s.id || s.SSN_IDF,
        group_id: s.group_id || s.GRP_IDF,
        group_name: s.group_name || s.GRP_NAME,
        course_title: s.course_title || s.CRS_TITLE,
        room_name: s.room_name || s.ARM_ROOM_NUMBER,
        date: dateStr,
        start_time: startTimeStr,
        end_time: endTimeStr,
        status: s.status || s.SSN_STATUS || 'SCHEDULED',
        status_reason: s.status_reason || s.SSN_STATUS_REASON,
        postponed_date: s.postponed_date || s.SSN_POSTPONED_DATE,
        postponed_time: s.postponed_time || s.SSN_POSTPONED_TIME,
        teacher_absent: s.teacher_absent ?? s.SSN_TEACHER_ABSENT,
        attendance_taken: s.attendance_taken ?? s.SSN_ATTENDANCE_TAKEN,
        attendances: s.attendances || [],
      };
    });
    setSessions(mappedSessions);

    // 6. Finances normalization
    const f = d.finances || {};
    setFinances({
      rate_type: f.rate_type || f.rateType || mappedTeacher.rate_type || 'hourly',
      hourly_rate: f.hourly_rate ?? f.hourlyRate ?? mappedTeacher.hourly_rate ?? 0,
      total_hours_month: f.total_hours_month ?? f.completedHours ?? 0,
      total_amount_month: f.total_amount_month ?? f.totalEarnings ?? 0,
      history: f.history || []
    });

    // 7. Absence requests
    const mappedAbs = (d.absenceRequests || d.absence_requests || []).map((ar: any) => ({
      ...ar,
      id: ar.id || ar.TAR_IDF,
      session_id: ar.session_id || ar.SSN_IDF,
      request_date: ar.request_date || ar.TAR_REQUEST_DATE,
      start_date: ar.start_date || (ar.SSN_START_HOUR ? new Date(ar.SSN_START_HOUR).toLocaleDateString('fr-CA') : ''),
      end_date: ar.end_date || (ar.SSN_END_HOUR ? new Date(ar.SSN_END_HOUR).toLocaleDateString('fr-CA') : ''),
      reason: ar.reason || ar.TAR_REASON,
      status: (ar.status || ar.TAR_STATUS || 'PENDING').toString().toUpperCase(),
      group_name: ar.group_name || ar.GRP_NAME,
      admin_comment: ar.admin_comment || ar.TAR_DECISION_NOTES || '',
    }));
    setAbsenceRequests(mappedAbs);
  }, []);

  // Load Dashboard data with token
  const loadDashboardData = useCallback(async (authToken: string) => {
    if (!authToken || authToken === 'undefined' || authToken === 'null') {
      localStorage.removeItem('blobtic_portal_teacher_token');
      setToken(null);
      setInitialLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await academyApi.portalTeacherGetData(authToken);
      if (res && res.success && res.data) {
        applyDashboardPayload(res.data);
      } else {
        localStorage.removeItem('blobtic_portal_teacher_token');
        setToken(null);
        setError(res?.error || res?.message || 'Session expirée. Veuillez vous reconnecter.');
      }
    } catch (err: any) {
      console.error('Error fetching teacher portal data:', err);
      setError('Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [applyDashboardPayload]);

  useEffect(() => {
    if (token) {
      loadDashboardData(token);
    } else {
      setInitialLoading(false);
    }
  }, [token, loadDashboardData]);

  // Handle Login
  const handleLogin = async (login: string, pass: string) => {
    setError(null);
    setLoading(true);
    setLastLoginIdent(login);
    try {
      const res = await academyApi.portalTeacherLogin(login, pass);
      if (res && res.success) {
        const authToken = res.token || res.data?.token;
        const requiresAca = res.requiresAcademySelection ?? res.data?.requiresAcademySelection ?? res.requires_academy_selection ?? res.data?.requires_academy_selection;
        const rawAcas = res.academies || res.data?.academies || [];
        const acas = rawAcas.map((a: any) => ({
          ...a,
          logo_base64: formatLogoUrl(a.logo_base64 || a.dosLogo || a.logo || a.DOS_LOGO)
        }));
        setAcademies(acas);

        if (requiresAca && acas.length > 1) {
          setShowAcademyModal(true);
        } else if (authToken) {
          localStorage.setItem('blobtic_portal_teacher_token', authToken);
          setToken(authToken);

          // If backend already included dashboard data in the login response
          const nestedData = res.data?.data;
          if (nestedData && (nestedData.teacher || nestedData.agency)) {
            applyDashboardPayload(nestedData);
          } else {
            await loadDashboardData(authToken);
          }
        }
      } else {
        setError(res?.message || res?.error || 'Identifiant ou mot de passe incorrect.');
      }
    } catch (err: any) {
      console.error('Teacher login error:', err);
      setError('Erreur de communication avec le serveur.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Academy Selection
  const handleSelectAcademy = async (dosId: number) => {
    setLoading(true);
    try {
      const loginIdent = lastLoginIdent || token || '';
      const res = await academyApi.portalTeacherSelectAcademy(loginIdent, dosId);
      if (res && res.success) {
        setShowAcademyModal(false);
        const authToken = res.token || res.data?.token;
        if (authToken) {
          localStorage.setItem('blobtic_portal_teacher_token', authToken);
          setToken(authToken);
          const nestedData = res.data?.data;
          if (nestedData && (nestedData.teacher || nestedData.agency)) {
            applyDashboardPayload(nestedData);
          } else {
            await loadDashboardData(authToken);
          }
        }
      } else {
        setError(res?.message || res?.error || "Erreur lors de la sélection de l'académie.");
      }
    } catch (err) {
      console.error('Select academy error:', err);
      setError("Erreur lors de la sélection de l'académie.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('blobtic_portal_teacher_token');
    setToken(null);
    setTeacher(null);
    setSelectedAcademy(null);
    setGroups([]);
    setSessions([]);
    setAbsenceRequests([]);
  };

  if (initialLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // Not authenticated
  if (!token || !teacher || !selectedAcademy) {
    return (
      <>
        <TeacherPortalLoginPage onLogin={handleLogin} loading={loading} error={error} />
        {showAcademyModal && (
          <TeacherAcademySelectModal
            open={showAcademyModal}
            academies={academies}
            onSelect={handleSelectAcademy}
            loading={loading}
          />
        )}
      </>
    );
  }

  // Authenticated Dashboard
  return (
    <>
      <TeacherPortalDashboard
        token={token}
        teacher={teacher}
        canTakeAttendance={canTakeAttendance}
        academy={selectedAcademy}
        academiesCount={academies.length}
        groups={groups}
        sessions={sessions}
        absenceRequests={absenceRequests}
        finances={finances}
        onSwitchAcademy={() => setShowAcademyModal(true)}
        onLogout={handleLogout}
        onRefresh={() => loadDashboardData(token)}
      />
      {showAcademyModal && (
        <TeacherAcademySelectModal
          open={showAcademyModal}
          academies={academies}
          onSelect={handleSelectAcademy}
          loading={loading}
        />
      )}
    </>
  );
};

export const TeacherPortalApp: React.FC = () => {
  return (
    <TeacherPortalLanguageProvider>
      <TeacherPortalInner />
    </TeacherPortalLanguageProvider>
  );
};

export default TeacherPortalApp;
